import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Alert, Card, CardContent, Chip } from '@mui/material';
import { CheckCircle, Error, Person, School, CalendarToday, Grade } from '@mui/icons-material';
import Header from './components/Header';
import axios from 'axios';

function Verify() {
  const [authCode, setAuthCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);
    setError(null);

    if (!authCode.trim()) {
      setError('Please enter an authenticity code.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/certificate/verify', {
        auth_code: authCode.trim(),
      });

      console.log('Verification response:', response.data);
      setResult(response.data);

      if (!response.data.found) {
        setError(response.data.message);
      }

    } catch (err) {
      console.error('Verification failed:', err);
      const errorMessage = err.response?.data?.detail || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{
          textAlign: 'center',
          color: 'white',
          fontWeight: 'bold',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
        }}>
          Certificate Verification
        </Typography>
        <Typography variant="body1" sx={{
          mb: 4,
          textAlign: 'center',
          color: 'rgba(255,255,255,0.9)',
          fontWeight: '500',
          textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
        }}>
          Enter a certificate authenticity code to verify its validity and view details.
        </Typography>

        <Card sx={{ mb: 4, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <CardContent>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <TextField
                id="authenticity-code"
                label="Authenticity Code"
                variant="outlined"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
                sx={{ width: '100%', maxWidth: '400px' }}
                placeholder="e.g., ABC12345"
                required
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ width: '100%', maxWidth: '400px' }}
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify Certificate'}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }} icon={<Error />}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>

        {result && result.found && result.certificate_data && (
          <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 28 }} />
                <Typography variant="h5" component="h2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  Certificate Verified Successfully!
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 3 }}>
                {/* Student Information */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Student Information
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    <strong>Name:</strong> {result.certificate_data.student_name || 'Not provided'}
                  </Typography>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    <strong>Email:</strong> {result.certificate_data.student_email}
                  </Typography>
                </Box>

                {/* Course Information */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <School sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Course Information
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4, mb: 1 }}>
                    <strong>Title:</strong> {result.certificate_data.course_title}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
                    {result.certificate_data.course_description}
                  </Typography>
                </Box>

                {/* Completion Details */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Completion Details
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    <strong>Completed:</strong> {formatDate(result.certificate_data.completion_date)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 4, mt: 1 }}>
                    <Grade sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body1">
                      <strong>Score:</strong> {result.certificate_data.score}%
                    </Typography>
                    <Chip
                      label={result.certificate_data.score >= 100 ? "Perfect Score" : "Passed"}
                      color={result.certificate_data.score >= 100 ? "success" : "primary"}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>

                {/* Authenticity Code */}
                <Box sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Authenticity Code:
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                    {result.certificate_data.authenticity_code}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <Typography variant="body2" sx={{
          mt: 3,
          textAlign: 'center',
          color: 'white',
          fontWeight: '600',
          textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          padding: '12px 24px',
          borderRadius: '16px',
          display: 'inline-block',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          💡 Tip: The authenticity code is located in the footer of any DeepAlert Training Academy certificate.
        </Typography>
      </Container>
    </Box>
  );
}

export default Verify;
